import React, { useEffect } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { AdvancedConfigV2 } from "src/aiNode/editorComponents/AdvancedConfigV2";
import { ModelSelector } from "src/aiNode/editorComponents/ModelSelector";
import { PromptEditorV2 } from "src/aiNode/editorComponents/PromptEditorV2";
import { ResponseMappingV2 } from "src/aiNode/editorComponents/ResponseMappingV2";
import {
  AiNodeJsonSchemaPropertyUI,
  AiNodeQuota,
  AiNodeV2Form,
  BasicModelCard,
} from "src/aiNode/types";
import {
  convertAiNodeV2BEToForm,
  convertAiNodeV2FormToBE,
} from "src/aiNode/utils";
import {
  useAiNodeQuotas,
  useAvailableAiModels,
} from "src/api/connectApi/queries";
import { useWorkspace } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import { LoadingView } from "src/base-components/LoadingView";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { AiNodeV2, AiNodeV2Data } from "src/constants/NodeDataTypes";
import { Accordion } from "src/design-system/Accordion";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

// Mirrors the backend's `RESPONSE_SCHEMA_FIELD_NAME`: the `field_name` it tags
// onto run errors raised while resolving the response JSON schema (e.g. a
// variable-sourced enum that resolves to a non-list value).
const RESPONSE_SCHEMA_FIELD_NAME = "Response schema";

type WrapperProps = {
  node: AiNodeV2;
  workspaceId: string;
} & NodeEditorBaseProps<AiNodeV2Data>;

export const AiNodeV2Editor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const availableModels = useAvailableAiModels(workspace.data?.base_url);
  const quotas = useAiNodeQuotas(workspace.data?.base_url);
  return (
    <LoadingView
      queryResult={[availableModels, quotas]}
      renderUpdated={([availableModels, quotas]: [
        BasicModelCard[],
        AiNodeQuota,
      ]) => (
        <AiNodeV2EditorInternal
          availableModels={availableModels}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          node={node}
          quotas={quotas}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

type Props = {
  node: AiNodeV2;
  availableModels: BasicModelCard[];
  quotas: AiNodeQuota;
} & NodeEditorBaseProps<AiNodeV2Data>;

const AiNodeV2EditorInternal: React.FC<Props> = ({
  availableModels,
  quotas,
  immutable,
  node,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const currentFormValues: AiNodeV2Form = convertAiNodeV2BEToForm(node.data);

  const formProps = useForm({
    defaultValues: currentFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Control the form state externally
  useEffect(() => {
    if (isReactive) {
      const incoming = convertAiNodeV2BEToForm(node.data);
      const existing = formProps.getValues();
      const merged = mergeFormValuesWithExisting(incoming, existing);
      formProps.reset(merged, {
        keepDirty: true,
        keepTouched: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReactive, node.data]);

  useSubmitForm({
    onChange: (data: AiNodeV2Form) => {
      onUpdate({
        newData: convertAiNodeV2FormToBE(data),
      });
    },
    disabled: isReactive,
    previousValues: currentFormValues,
    subscribe: formProps.subscribe,
  });

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  // Response-schema errors are scoped to the whole schema, so the backend keys
  // them by node id with `field_name = "Response schema"` rather than an output
  // location id. That means they never match the per-field `runFieldErrors`
  // lookup, so we pull them out by field name here (there can be several) and
  // surface them in the response section.
  const responseSchemaErrors = displayedError?.field_errors
    ?.filter((error) => error.field_name === RESPONSE_SCHEMA_FIELD_NAME)
    .map((error) => error.message);

  return (
    <FormProvider {...formProps}>
      <form>
        <Accordion defaultValue={["model", "prompt", "response", "config"]}>
          <Accordion.Item
            isReadOnly={immutable || renderedInDiffView}
            label="Model details"
            value="model"
          >
            <ModelSelector
              immutable={immutable}
              models={availableModels}
              quotas={quotas}
            />
          </Accordion.Item>
          <Accordion.Item
            isReadOnly={immutable || renderedInDiffView}
            label="Prompt"
            value="prompt"
          >
            <PromptEditorV2
              immutable={immutable}
              isReactive={isReactive}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
          <Accordion.Item
            isReadOnly={immutable || renderedInDiffView}
            label="Configure AI response"
            value="response"
          >
            <ResponseMappingV2
              immutable={immutable}
              responseSchemaErrors={responseSchemaErrors}
              runFieldErrors={runFieldErrors}
              onUpdate={onUpdate}
            />
          </Accordion.Item>
          <Accordion.Item
            isReadOnly={immutable || renderedInDiffView}
            label="Advanced settings"
            value="config"
          >
            <AdvancedConfigV2
              immutable={immutable}
              isReactive={isReactive}
              models={availableModels}
              runFieldErrors={runFieldErrors}
            />
          </Accordion.Item>
        </Accordion>
      </form>
    </FormProvider>
  );
};

// Consolidate local form state with incoming controlled state.
// This allows keeping empty fields entirely in local state, while still controlling the form with external server state.
// If we don't do this, empty fields in local state would get replaced and suddenly disappear when the component
// rerenders with controlled state.
export const mergeFormValuesWithExisting = (
  incoming: AiNodeV2Form,
  existing: AiNodeV2Form,
): AiNodeV2Form => {
  const merged: AiNodeV2Form = { ...incoming };

  const existingProps =
    existing.inference.response_format.json_schema?.properties ?? [];
  const incomingProps =
    incoming.inference.response_format.json_schema?.properties ?? [];
  const extras = existingProps.filter(
    (field) => field.fieldName == "",
  ) as AiNodeJsonSchemaPropertyUI[];

  const jsonSchema = merged.inference.response_format.json_schema;
  if (jsonSchema) {
    jsonSchema.properties = [...incomingProps, ...extras];
  }

  return merged;
};
